import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { ArrowUp, X } from 'lucide-react';
import Navigation from './components/Navigation';
import Hero from './components/Hero';
import Footer from './components/Footer';
import Services from './pages/Services';
import SovereignAI from './pages/SovereignAI';
import SemanticLakehouse from './pages/SemanticLakehouse';
import PrecisionRAG from './pages/PrecisionRAG';
import AgenticDashboard from './pages/AgenticDashboard';
import Careers from './pages/Careers';
import './App.css';

function BackToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setVisible(window.scrollY > 300);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!visible) return null;

  return (
    <button
      type="button"
      onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })}
      className="fixed bottom-6 right-6 z-50 inline-flex items-center justify-center w-11 h-11 rounded-full bg-orange-500 text-white shadow-lg shadow-orange-500/30 hover:bg-orange-600 transition-colors"
      aria-label="Back to top"
    >
      <ArrowUp className="w-5 h-5" />
    </button>
  );
}

function PrivacyModal({ open, onClose }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-label="Close privacy policy"
      />
      <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 sm:p-7">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Privacy Policy</h3>
          <p className="text-sm text-gray-600 leading-relaxed mb-4">
            This Privacy Policy describes how BeeMind AI Private Limited collects and uses information on this website.
          </p>
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p>
              <span className="font-semibold">Information we collect:</span> We may collect information you provide via
              forms (such as name, email, and resume) and basic technical data (such as browser type and approximate
              location derived from IP) to operate and secure the site.
            </p>
            <p>
              <span className="font-semibold">How we use information:</span> To respond to inquiries, evaluate
              applications, provide requested demos, improve site performance, and maintain security.
            </p>
            <p>
              <span className="font-semibold">No scraping / automated collection:</span> You may not use bots,
              scrapers, automated scripts, or similar means to access, extract, harvest, or collect content or data from
              this website without prior written permission.
            </p>
            <p>
              <span className="font-semibold">Sharing:</span> We do not sell personal information. We may use service
              providers to process form submissions or maintain infrastructure. These providers are used only to deliver
              the service.
            </p>
            <p>
              <span className="font-semibold">Security:</span> We use reasonable safeguards to protect your
              information, but no method of transmission or storage is 100% secure.
            </p>
            <p>
              <span className="font-semibold">Contact:</span> For questions, contact{' '}
              <a className="font-semibold text-orange-600 hover:text-orange-700" href="mailto:info@beemind.ai">
                info@beemind.ai
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function DemoModal({ open, onClose }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-label="Close demo request modal"
      />
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 sm:p-7">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Request a Demo</h3>
          <p className="text-sm text-gray-600 leading-relaxed">
            Email the request to{' '}
            <a
              className="font-semibold text-orange-600 hover:text-orange-700"
              href="mailto:info@beemind.ai"
            >
              info@beemind.ai
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}

function AppShell() {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  useEffect(() => {
    if (hash === '#demo') {
      setDemoOpen(true);
      setPrivacyOpen(false);
      return;
    }

    if (hash === '#privacy') {
      setDemoOpen(false);
      setPrivacyOpen(true);
      const t = window.setTimeout(() => {
        setPrivacyOpen(false);
        navigate(pathname, { replace: true });
      }, 3000);
      return () => window.clearTimeout(t);
    }

    setDemoOpen(false);
    setPrivacyOpen(false);

    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [pathname, hash]);

  const closeDemo = () => {
    setDemoOpen(false);
    if (hash === '#demo') {
      navigate(pathname, { replace: true });
    }
  };

  const closePrivacy = () => {
    setPrivacyOpen(false);
    if (hash === '#privacy') {
      navigate(pathname, { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-navy-950 text-slate-100 app-tech-bg app-frame overflow-hidden">
      <Navigation />
      <Routes>
        <Route path="/" element={<Hero />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/sovereign-ai" element={<SovereignAI />} />
        <Route path="/services/semantic-lakehouse" element={<SemanticLakehouse />} />
        <Route path="/services/precision-rag" element={<PrecisionRAG />} />
        <Route path="/services/agentic-dashboard" element={<AgenticDashboard />} />
        <Route path="/careers" element={<Careers />} />
      </Routes>
      <Footer />
      <BackToTop />
      <DemoModal open={demoOpen} onClose={closeDemo} />
      <PrivacyModal open={privacyOpen} onClose={closePrivacy} />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}

export default App;
